import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';

import i18n from 'i18n-js';
import Highlighter from 'react-highlight-words';
import {
    alpha,
    Button,
    Card,
    Checkbox,
    IconButton,
    Dialog,
    DialogTitle,
    DialogActions,
    DialogContent,
    InputAdornment,
    FormControl,
    InputLabel,
    Input,
    FormControlLabel,
    Box,
    Typography,
} from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import LoyaltyIcon from '@material-ui/icons/Loyalty';
import CreateIcon from '@material-ui/icons/Create';
import UndoIcon from '@material-ui/icons/Undo';
import dayjs from 'dayjs';
import clsx from 'clsx';
import * as yup from 'yup';

import categoryStore from 'store/categoryStore';
import { default as ordersStore } from 'store/ordersStore';
import { images } from 'assets';
import {
    OrderItemStatus,
    OrderStatus,
    Roles,
    OrderServices,
    Positions,
    MenuType,
} from 'constants/enums';
import { OrderItemStatusChangeModel, OrderUpdateModel } from 'api/models';
import { priceStyleHelper } from 'helpers/helperFunctions';
import { theme } from 'config/theme';
import { PopupConfirmation, SectionTab, Text } from 'components/shared';
import {
    OrderStatusItem,
    ServiceType,
    DeliveryTime,
    OrderTime,
    OrderPayment,
    OrderQRLeftCode,
    ResponsibleUserSelect,
    PriceLevelSelect,
    OrderDailyMenuItem,
    PriceText,
    OrderItemCard,
    StornoModal,
} from './components';

import { Input as BaseInput } from 'components/shared';
import { useStyles } from './styles';
import MenuIngredientManagement from 'components/menu/MenuManagementTable/components/MenuIngredientManagement';
import { useAppSelector } from 'index';
import { Formik, Form } from 'formik';
import { OrderItem, OrderDetailsItem, ModifiedIngredient } from 'store/ordersStore/reducer';
import agent from 'api/agent';
import globalStore from 'store/globalStore';
import MenusModal from 'components/menu/Modals/MenusModal';
import restaurantDetailsStore from 'store/restaurantDetailsStore';
import { LocalStorageKeys } from 'constants/localStorageKeys';

const dailyMenuKey = 'Daily-Menu-Key';
const allItemsKey = 'All-Items-Key';
const courseMenuKey = 'Course-Menu-Key';

type FilterSelection = {
    id: string;
    name: string;
};

//TODO: need to refactor and simplify logic
export default function OrderDetails() {
    const classes = useStyles();

    const dispatch = useDispatch();
    const orders = useAppSelector((state) => state.orders);
    const orderDetails = useAppSelector((state) => state.orders.orderDetails);
    const category = useAppSelector((state) => state.category);
    const user = useAppSelector((state) => state.user);
    const currentRestaurant = useAppSelector((state) =>
        state.user.restaurants.find((rest) => rest.id === state.user.currentRestaurantId)
    );
    const currentPosition = useAppSelector(
        (state) =>
            state.user.restaurants.find((rest) => rest.id === state.user.currentRestaurantId)
                ?.currentPosition || Positions.WAITER
    );
    const itemsToAdd = useAppSelector((state) => state.orders.itemsToAdd);
    const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);
    const stornoReasons = useAppSelector((state) => state.restaurantDetails.stornoReasons);
    const language = useAppSelector((state) => state.global.language);

    const [selectedFilter, setSelectedFilter] = useState<FilterSelection[]>([]);
    const [showFilter, setShowFilter] = useState(false);
    const [editOrder, setEditOrder] = useState(false);
    const [hasChanged, setHasChanged] = useState(false);
    const [showMenus, setShowMenus] = useState(false);
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(0);
    const [saved, setSaved] = useState(false);
    const [showIngredients, setShowIngredients] = useState(false);
    const [canEditOrder, setCanEditOrder] = useState(false);
    const [editedComment, setEditedComment] = useState<string>('');
    const [ableToChangeStatus, setAbleToChangeStatus] = useState(false);
    const [showDiscountModal, setShowDiscountModal] = useState(false);
    const [serviceKey, setServiceKey] = useState(
        orderDetails?.service.charAt(0).toLowerCase() + orderDetails?.service.slice(1)
    );
    const showStornoModal = useAppSelector((state) => state.orders.showStornoModal);
    const currentLanguage = useRef(language);
    const listRef = useRef<HTMLDivElement>(null);
    const initialOrderItems = useRef<OrderItem[]>([]);
    const cachedFiltersString = localStorage.getItem(LocalStorageKeys.barmenFilter);
    const cachedFilters: FilterSelection[] =
        cachedFiltersString !== null ? JSON.parse(cachedFiltersString) : [];
    const currentOrderWorkAreas = orderDetails.orderItems.reduce((prev, item) => {
        if (
            item.dishes?.length &&
            !prev.some((f) => f.id === dailyMenuKey) &&
            item.menuType !== MenuType.MultiCourse
        ) {
            prev.push({ id: dailyMenuKey, name: i18n.t('restaurant.dailyMenu') });
        } else if (
            item.dishes?.length &&
            !prev.some((f) => f.id === courseMenuKey) &&
            item.menuType === MenuType.MultiCourse
        ) {
            prev.push({ id: courseMenuKey, name: i18n.t('restaurant.multiCourseMenu') });
        } else if (item.categoryId && !prev.some((f) => f.id === item.categoryId)) {
            prev.push({ id: item.categoryId, name: item.categoryName });
        }
        return prev;
    }, [] as FilterSelection[]);

    useEffect(() => {
        category.categoryDetails === null &&
            dispatch(categoryStore.actionCreators.getCategoryDetails());
        if (!!orders.orderDetailsId) {
            refreshCurrentState();
        }
    }, [
        orders.orderDetailsId,
        orderDetails.status === OrderStatus.Cancelled,
        orderDetails.status === OrderStatus.Closed,
        orders.needToUpdate,
        orders.orderDetails.deliveryTime,
    ]);

    useEffect(() => {
        if (listRef.current !== null) {
            listRef.current.scrollTop = 0;
        }
        setServiceKey(
            orderDetails?.service.charAt(0).toLowerCase() + orderDetails?.service.slice(1)
        );
    }, [orders.orderDetailsId]);

    useEffect(() => {
        if (!!!restaurant.id && !!currentRestaurant?.id) {
            dispatch(restaurantDetailsStore.actionCreators.getRestaurantInformation());
        }
    }, [currentRestaurant?.id]);

    useEffect(() => {
        setCanEditOrder(currentRestaurant?.currentPosition !== Positions.COOK);
        const position = user.restaurants.find(
            (x) => x.id === user.currentRestaurantId
        )?.currentPosition;
        setAbleToChangeStatus(position !== Positions.COOK);
    }, [currentRestaurant?.currentPosition]);

    useEffect(() => {
        cacheFilters(selectedFilter);
    }, [selectedFilter]);

    useEffect(() => {
        if (currentPosition === Positions.BARMAN && cachedFilters.length && !showFilter) {
            setShowFilter(true);
        }
    }, []);

    useEffect(() => {
        (!!!stornoReasons?.length || currentLanguage.current !== language) &&
            dispatch(restaurantDetailsStore.actionCreators.getStornoReasons());
        currentLanguage.current = language;
    }, [language]);

    const validationSchema = yup.object().shape({
        comment: yup.string().required().max(256),
    });

    function refreshCurrentState() {
        if (orders.needToUpdate || orders.orderDetailsId !== orderDetails.id) {
            dispatch(globalStore.actionCreators.showSpiner());
            agent.Order.GetOrder(orders.orderDetailsId)
                .then((res) => {
                    initialOrderItems.current = [...res.orderItems];
                    dispatch(ordersStore.actionCreators.setOrderDetails({ ...res }));
                })
                .catch((e) => {
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => dispatch(globalStore.actionCreators.hideSpiner()));
        } else {
            initialOrderItems.current = [...orderDetails.orderItems];
        }

        setEditOrder(false);
        if (currentPosition === Positions.BARMAN) {
            setSelectedFilter(Array.isArray(cachedFilters) ? cachedFilters : []);
        } else {
            setShowFilter(false);
            setSelectedFilter([]);
        }
    }

    const showAddButton = (isEmpty: boolean) =>
        canEditOrder &&
        (editOrder ||
            (!!orders.orderDetailsId && isEmpty && orderDetails.status !== OrderStatus.Cancelled));

    function handleCheckingItems(
        id: string,
        uniqueId: string,
        status: OrderItemStatus,
        dailyMenuUniqueId?: string
    ) {
        const model: OrderItemStatusChangeModel = {
            itemId: id,
            itemUniqueId: uniqueId,
            dailyMenuUniqueId: dailyMenuUniqueId,
            status,
        };
        /* let updatedOrder = {
            ...orderDetails,
            orderItems: orderDetails.orderItems.map((x) =>
                x.id === id && x.dailyMenuId === dailyMenuId
                    ? {
                          ...x,
                          status:
                              x.status !== OrderItemStatus.Ready
                                  ? OrderItemStatus.Ready
                                  : OrderItemStatus.Order,
                      }
                    : x
            ),
        };
        if (
            updatedOrder.orderItems.filter((x) => x.status === OrderItemStatus.Ready).length ===
            updatedOrder.orderItems.length
        ) {
            updatedOrder.status = OrderStatus.Ready;
        } else {
            updatedOrder.status = OrderStatus.InProgress;
        } */
        dispatch(ordersStore.actionCreators.changeOrderItemStatus(orderDetails.id, model));
    }

    function handleSelectingFilter(filter: FilterSelection) {
        const newFilters = selectedFilter.some((f) => f.id === filter.id)
            ? selectedFilter.filter((x) => x.id !== filter.id)
            : [...selectedFilter, filter];
        setSelectedFilter(newFilters);
    }

    function cacheFilters(filters: FilterSelection[]) {
        if (currentPosition === Positions.BARMAN) {
            localStorage.setItem(LocalStorageKeys.barmenFilter, JSON.stringify(filters));
        }
    }

    /* function resetEditedOrders() {
        let editedOrder = { ...orderDetails };
        editedOrder.orderItems.forEach((item) => {
            item.modifiedByManagement = 0;
        });
        dispatch(OrdersStore.actionCreators.setOrderDetails(editedOrder));
    } */

    function saveChanges(model: OrderUpdateModel | null = null) {
        if (!model) {
            model = {
                orderItems: [...orderDetails.orderItems],
                deliveryTime: orderDetails.deliveryTime,
                status: OrderStatus.Order,
                paymentMethod: orderDetails.paymentMethod,
                qrCodeName: orderDetails.qrCodeName,
                service: orderDetails.service,
                priceLevelId: orderDetails.priceLevelId || '',
            };
            if (itemsToAdd.length) {
                itemsToAdd.forEach((item) => {
                    const existsItem = model!.orderItems.find(
                        (it) => it.id === item.id && it.status === OrderItemStatus.Order && !it.isPaid
                    );
                    if (existsItem) {
                        if (
                            JSON.stringify(existsItem.modifiedIngredients) ===
                                JSON.stringify(item.modifiedIngredients || []) &&
                            existsItem.size === item.size
                        ) {
                            const index = model!.orderItems.indexOf(existsItem);
                            if (index !== -1) {
                                model!.orderItems[index].amount += item.amount;
                            }
                        } else {
                            model!.orderItems.push(item);
                        }
                    } else {
                        model!.orderItems.push(item);
                    }
                });
            }
        }
        if (model.status === OrderStatus.Cancelled)
            dispatch(
                ordersStore.actionCreators.setOrderDetails({
                    ...orderDetails,
                    status: OrderStatus.Cancelled,
                })
            );
        dispatch(
            ordersStore.actionCreators.updateOrder(orderDetails.id, model, () => {
                setSaved(true);
                //resetEditedOrders();
                setEditOrder(false);
                setShowMenus(false);
            })
        );
        //setInitialOrderState({ ...initialOrder });
        //setTimeout(refreshCurrentState, 500);
    }

    function saveComment(comment: string) {
        const updatedCommentItem = orderDetails.orderItems.find(
            (x) => x.uniqueId === editedComment
        );
        if (updatedCommentItem) {
            updatedCommentItem.comment = comment;
        }

        const model = {
            orderItems: orderDetails.orderItems.map((item) =>
                item.uniqueId === updatedCommentItem?.uniqueId ? updatedCommentItem! : item
            ),
            deliveryTime: orderDetails.deliveryTime,
            status: orderDetails.status,
            paymentMethod: orderDetails.paymentMethod,
            qrCodeName: orderDetails.qrCodeName,
            service: orderDetails.service,
            priceLevelId: orderDetails.priceLevelId || '',
        };
        dispatch(
            ordersStore.actionCreators.updateOrder(orderDetails.id, model, () => {
                setSaved(true);
                setEditedComment('');
                setEditOrder(false);
            })
        );
    }

    function removeComment() {
        const updatedCommentItem = orderDetails.orderItems.find(
            (x) => x.uniqueId === editedComment
        )!;

        if (!updatedCommentItem.comment) {
            setEditedComment('');
        } else {
            updatedCommentItem.comment = '';
            const model = {
                orderItems: orderDetails.orderItems.map((item) =>
                    item.uniqueId === updatedCommentItem?.uniqueId ? updatedCommentItem! : item
                ),
                deliveryTime: orderDetails.deliveryTime,
                status: orderDetails.status,
                paymentMethod: orderDetails.paymentMethod,
                qrCodeName: orderDetails.qrCodeName,
                service: orderDetails.service,
                priceLevelId: orderDetails.priceLevelId || '',
            };
            dispatch(
                ordersStore.actionCreators.updateOrder(orderDetails.id, model, () => {
                    setSaved(true);
                    setEditedComment('');
                    setEditOrder(false);
                })
            );
        }
    }

    function checkIsEdititngDisabled() {
        return (
            !orders.orderDetailsId ||
            orderDetails.status === OrderStatus.Closed ||
            orderDetails.status === OrderStatus.Cancelled ||
            (orderDetails.isPaid && orderDetails.status !== OrderStatus.Ready)
        );
    }

    function cancelChanges() {
        setHasChanged(false);
        //dispatch(OrdersStore.actionCreators.resetOrderDetails());
        !saved &&
            dispatch(
                ordersStore.actionCreators.setOrderDetails({
                    ...orderDetails,
                    orderItems: [...initialOrderItems.current],
                    //orderItems: _.cloneDeep(initialOrderState.orderItems),
                })
            );
        setEditOrder(false);
        setShowCancelConfirmation(0);
    }

    function showIngredientsEditIcon(dish: OrderItem) {
        return (
            !orderDetails.isPaid &&
            orderDetails.status !== OrderStatus.Cancelled &&
            dish.status !== OrderItemStatus.Ready &&
            dish.status !== OrderItemStatus.Cancelled &&
            !!dish.ingredients?.find((i) => i.isAddition || i.isRemoval)
        );
    }

    function setDeliveryTime(time: Date) {
        const newTime = dayjs(time).valueOf();
        if (orderDetails.deliveryTime !== newTime) {
            dispatch(
                ordersStore.actionCreators.setOrderDetails({
                    ...orderDetails,
                    deliveryTime: newTime,
                    status:
                        orderDetails.status === OrderStatus.Order
                            ? OrderStatus.InProgress
                            : orderDetails.status,
                })
            );
            saveChanges({
                orderItems: orderDetails.orderItems,
                deliveryTime: newTime,
                paymentMethod: orderDetails.paymentMethod,
                status: OrderStatus.Order,
                qrCodeName: orderDetails.qrCodeName,
                service: orderDetails.service,
                priceLevelId: orderDetails.priceLevelId || '',
            });
        }
    }

    /* function changePaymentMethod(method: PaymentMethod) {
        saveChanges({
            orderItems: orderDetails.orderItems,
            deliveryTime: orderDetails.deliveryTime,
            paymentMethod: method,
            status: OrderStatus.Non,
            qrCodeName: orderDetails.qrCodeName,
            service: orderDetails.service,
        });
        dispatch(
            OrdersStore.actionCreators.setOrderDetails({ ...orderDetails, paymentMethod: method })
        );
    } */

    function changeServiceTypeMethod(service: OrderServices) {
        dispatch(ordersStore.actionCreators.UpdateOrderService(orderDetails.id, service));
    }

    function changeOrderQRCode(qrCodeName: string) {
        saveChanges({
            orderItems: orderDetails.orderItems,
            deliveryTime: orderDetails.deliveryTime,
            paymentMethod: orderDetails.paymentMethod,
            status: OrderStatus.Order,
            qrCodeName: qrCodeName,
            service: orderDetails.service,
            priceLevelId: orderDetails.priceLevelId || '',
        });
        dispatch(
            ordersStore.actionCreators.setOrderDetails({ ...orderDetails, qrCodeName: qrCodeName })
        );
    }

    function prepareDishesData() {
        let result: {
            name: string;
            items: OrderItem[];
            dailyMenu?: OrderDetailsItem;
            guestNumber: number;
        }[] = [];
        orderDetails.orderItems.forEach((item) => {
            if (item.dishes?.length > 0) {
                const dailyMenuCategoryName = item.name || dailyMenuKey + ' ' + item.id;
                result.push({
                    name: dailyMenuCategoryName,
                    items: [...item.dishes],
                    dailyMenu: { ...item },
                    guestNumber: 0,
                });
            } else if (Boolean(item.guestNumber)) {
                const existing = result.find((x) => x.guestNumber === item.guestNumber);
                if (existing) {
                    existing.items.push({ ...item });
                } else {
                    result.push({
                        name: `${i18n.t('common.guest')} ${item.guestNumber}`,
                        items: [{ ...item }],
                        guestNumber: item.guestNumber!,
                    });
                }
            } else {
                if (result.find((x) => x.name === item.categoryName)) {
                    result.find((x) => x.name === item.categoryName)?.items.push({ ...item });
                } else {
                    result.push({ name: item.categoryName, items: [{ ...item }], guestNumber: 0 });
                }
            }
        });

        result = result.sort((a, b) => {
            const getPriority = (obj: {
                name: string;
                items: OrderItem[];
                dailyMenu?: OrderDetailsItem | undefined;
            }) => {
                if (obj.items.every((item) => item.isPaid) || obj.dailyMenu?.isPaid) return 0;
                if (obj.items.some((item) => item.isPaid)) return 1;
                return 2;
            };

            return getPriority(a) - getPriority(b);
        });

        return result;
    }

    function checkIsDisabled(dishStatus: OrderItemStatus, categoryId: string) {
        if (dishStatus === OrderItemStatus.Storno) {
            return true;
        }
        if ((restaurant as any)?.[serviceKey]?.isDeliverAnytime) {
            return (
                dishStatus === OrderItemStatus.Ready ||
                dishStatus === OrderItemStatus.Delivered ||
                orderDetails.status === OrderStatus.Closed ||
                orderDetails.status === OrderStatus.Cancelled
            );
        } else {
            return (
                orderDetails.status === OrderStatus.Cancelled ||
                (orderDetails.service !== OrderServices.DineIn && !orderDetails.isPaid) ||
                currentRestaurant?.currentCategory !== categoryId ||
                dishStatus === OrderItemStatus.Ready ||
                dishStatus === OrderItemStatus.Delivered ||
                orderDetails.status === OrderStatus.Ready ||
                orderDetails.status === OrderStatus.Closed
            );
        }
    }

    function checkIsDisableDailyMenuItem(dish: OrderItem) {
        let isEnabled = false;
        const position = user.restaurants.find(
            (x) => x.id === user.currentRestaurantId
        )?.currentPosition;
        if (position === Positions.COOK) {
            return !isEnabled;
        }
        if (orderDetails.service !== OrderServices.DineIn && !orderDetails.isPaid) {
            return !isEnabled;
        }
        if (dish.status === OrderItemStatus.Delivered) {
            return !isEnabled;
        }
        if (dish.status === OrderItemStatus.Order) {
            const categoryId = category.categoryDetails?.dishes.find(
                (x) => x.id === dish.id
            )?.categoryId;
            if (categoryId) {
                isEnabled =
                    category.categoryDetails?.categories.find((x) => x.id === categoryId)
                        ?.position ===
                        user.restaurants.find((x) => x.id === user.currentRestaurantId)
                            ?.currentPosition ||
                    (!!(restaurant as any)?.[serviceKey]?.isDeliverAnytime &&
                        currentRestaurant?.currentPosition !== Positions.COOK);
            }
        }

        return !isEnabled;
    }

    function checkIsAllowed(item: OrderItem) {
        return (
            !item.isPaid &&
            (item.status === OrderItemStatus.Order ||
                item.status === OrderItemStatus.ReadyToPrepare)
        );
    }

    function handleIngredientsClose() {
        const editedDish = orderDetails.orderItems.find((x) => x.id === orders.selectedDishId);
        const defaultDish = initialOrderItems.current.find((x) => x.id === orders.selectedDishId);
        if (
            JSON.stringify(editedDish?.modifiedIngredients) ===
                JSON.stringify(defaultDish?.modifiedIngredients) &&
            editedDish?.price === defaultDish?.price
        ) {
            setShowIngredients(false);
        } else {
            setShowCancelConfirmation(1);
        }
    }

    function getFilters() {
        const filters = [];
        const isSameFilters =
            new Set(currentOrderWorkAreas.map((f) => f.id)).difference(
                new Set(selectedFilter.map((f) => f.id))
            ).size === 0;
        filters.push(
            <div
                key={allItemsKey}
                className={clsx(classes.categoryFilterItem, {
                    selected: isSameFilters,
                })}
                onClick={() => {
                    const newFilters = isSameFilters
                        ? []
                        : [...currentOrderWorkAreas, ...cachedFilters];
                    setSelectedFilter(newFilters);
                }}
            >
                {i18n.t('common.all')}
            </div>
        );

        cachedFilters.forEach((filter) => {
            if (currentOrderWorkAreas.some((f) => f.id === filter.id)) return;

            filters.push(
                <div
                    key={filter.id}
                    className={clsx(classes.categoryFilterItem, {
                        selected: selectedFilter.some((f) => f.id === filter.id),
                    })}
                    onClick={() => handleSelectingFilter(filter)}
                >
                    {filter.name}
                </div>
            );
        });

        currentOrderWorkAreas.forEach((area) => {
            filters.push(
                <div
                    key={area.id}
                    className={clsx(classes.categoryFilterItem, {
                        selected: selectedFilter.some((f) => f.id === area.id),
                    })}
                    onClick={() => handleSelectingFilter(area)}
                >
                    {area.name}
                </div>
            );
        });

        return filters;
    }

    function prepareOrderItemsHeader() {
        const isEmpty = orderDetails.orderItems.length === 0;
        let actionsElements = [];
        /* if (
            !orderDetails.isPaid &&
            orderDetails.status !== OrderStatus.Cart &&
            orderDetails.status !== OrderStatus.Closed &&
            orderDetails.status !== OrderStatus.Cancelled &&
            !editOrder
        ) {
            actionsElements.push(
                <IconButton onClick={() => setShowDiscountModal(true)}>
                    <LoyaltyIcon style={{ color: theme.palette.secondary.dark, height: 20 }} />
                </IconButton>
            );
        } */
        if (!isEmpty && !editOrder && orderDetails.status !== OrderStatus.Cancelled) {
            actionsElements.push(
                <IconButton
                    onClick={() => setShowFilter(!showFilter)}
                    className={`${
                        !showFilter && selectedFilter.length > 0 ? classes.selectedFilterIcon : ''
                    } ${!orders.orderDetailsId ? classes.filterDisabled : ''}`}
                >
                    <FilterListIcon
                        style={{
                            //marginRight: 40,
                            color: showFilter
                                ? theme.palette.primary.main
                                : theme.palette.secondary.dark,
                        }}
                    />
                </IconButton>
            );
            if (canEditOrder) {
                actionsElements.push(
                    <IconButton
                        onClick={() => {
                            setShowFilter(false);
                            setEditOrder(true);
                        }}
                        style={{ marginLeft: 5 }}
                    >
                        <CreateIcon
                            className={clsx(classes.orderEditButton, {
                                Disabled: checkIsEdititngDisabled(),
                            })}
                            style={{
                                color: theme.palette.secondary.dark,
                            }}
                        />
                    </IconButton>
                );
            }
        }
        if (
            currentPosition !== Positions.COOK &&
            !isEmpty &&
            !editOrder &&
            orderDetails.status !== OrderStatus.Cancelled &&
            orderDetails.orderItems.some(
                (oi) =>
                    oi.status !== OrderItemStatus.Storno &&
                    (currentPosition === Positions.MANAGER ||
                        !oi.isPaid ||
                        oi.dishes?.some((dish) => !dish.isPaid))
            )
        ) {
            actionsElements.push(
                <IconButton
                    style={{ marginLeft: 5 }}
                    onClick={() => dispatch(ordersStore.actionCreators.SetShowStornoModal(true))}
                >
                    <UndoIcon style={{ color: theme.palette.secondary.dark }} />
                </IconButton>
            );
        }

        return (
            <>
                <div>
                    <div className={classes.orderItemsDetailsHeader}>
                        <div className={classes.orderDetailsHeadingText}>
                            {i18n.t('common.listOfOrders')}
                        </div>

                        {user.role === Roles.EMPLOYEE &&
                            !!orders.orderDetailsId &&
                            isEmpty &&
                            orderDetails.status !== OrderStatus.Cancelled && (
                                <div>
                                    <Button color="primary" onClick={() => cancelChanges()}>
                                        {i18n.t('button.cancel')}
                                    </Button>
                                    <Button
                                        disabled={!hasChanged}
                                        variant="contained"
                                        color="primary"
                                        onClick={() => saveChanges()}
                                    >
                                        {i18n.t('button.save')}
                                    </Button>
                                </div>
                            )}
                        {canEditOrder && !isEmpty && editOrder && (
                            <div className={classes.buttonsContainer} style={{ display: 'flex' }}>
                                <Button
                                    color="primary"
                                    onClick={() => cancelChanges()}
                                    style={{ marginRight: 5 }}
                                >
                                    {i18n.t('button.cancel')}
                                </Button>
                                <Button
                                    disabled={!hasChanged}
                                    variant="contained"
                                    color="primary"
                                    onClick={() => saveChanges()}
                                >
                                    {i18n.t('button.save')}
                                </Button>
                            </div>
                        )}
                        {actionsElements.length > 0 ? (
                            <div className={classes.oderActions} style={{ display: 'flex' }}>
                                {actionsElements}
                            </div>
                        ) : null}
                    </div>
                    {showAddButton(isEmpty) && (
                        <SectionTab
                            text={i18n.t('button.addNewItem')}
                            onClick={() => {
                                setShowMenus(true);
                                setSaved(false);
                            }}
                            disabled={false}
                        />
                    )}
                </div>
                {showFilter && (
                    <div className={classes.categoryFilterContainer}>{getFilters()}</div>
                )}
            </>
        );
    }

    function HighlitedText(text: string) {
        return (
            <Highlighter
                highlightClassName={classes.highlighter}
                searchWords={[orders.searchString]}
                autoEscape={true}
                textToHighlight={text}
            />
        );
    }

    /* function checkIsPaymentDisabled() {
        const status =
            orders.orders.filter((x) => x.id === orderDetails.id)[0]?.status ?? orderDetails.status;
        return (
            user.role === Roles.OWNER ||
            status === OrderStatus.Cancel ||
            status === OrderStatus.Closed ||
            orders.orders.length === 0
        );
    } */

    function renderModifiedIngredient(ingredient: ModifiedIngredient, dish: OrderItem) {
        let targetIngredient = dish.ingredients?.find((ing) => ingredient.id === ing.id);

        if (!targetIngredient) {
            targetIngredient = ingredient;
        }
        const val = ingredient.hasOwnProperty('isAdditionChecked')
            ? ingredient.isAdditionChecked
            : ingredient.isRemovalChecked;
        return (
            <div
                key={ingredient.id}
                className={classes.changeOfIngredientChip}
                style={{
                    backgroundColor: ingredient.isAdditionChecked
                        ? alpha(theme.palette.success.main, 0.9)
                        : alpha(theme.palette.error.main, 0.9),
                }}
            >
                {`${val ? '+' : '-'}
        ${targetIngredient.amount}${targetIngredient.unitOfMeasure ?? 'gr'} ${
                    targetIngredient.name ?? ingredient.name
                }`}
                <span style={{ marginLeft: 5 }}>{`${targetIngredient.price} CHF`}</span>
            </div>
        );
    }

    function renderEditedIngredients(dish: OrderItem, dailyMenuId?: string) {
        return (
            <div
                className={classes.changeOfIngredientsContainer}
                style={{
                    marginTop: dish.size || dish.amount > 1 ? 8 : 0,
                }}
            >
                <div style={{ height: 10 }} />
                <div className={classes.flexRowSpaceBetween}>
                    <span className={classes.orderDetailsHeadingText}>
                        {i18n.t('restaurant.changeOfIngredients')}
                    </span>
                    {showIngredientsEditIcon(dish) && (
                        <CreateIcon
                            className={classes.orderEditButton}
                            onClick={() => {
                                dispatch(
                                    ordersStore.actionCreators.setSelectedDish(dish.id, dailyMenuId)
                                );
                                setShowIngredients(true);
                            }}
                        />
                    )}
                </div>

                <div className={classes.changeOfIngredientsChipContainer}>
                    {dish?.modifiedIngredients?.map((ingredient) =>
                        renderModifiedIngredient(ingredient, dish)
                    )}
                </div>
            </div>
        );
    }

    function checkIsDeliveryDisabled(dish: OrderItem) {
        if (dish.status === OrderItemStatus.Storno) {
            return true;
        }
        if ((restaurant as any)?.[serviceKey]?.isDeliverAnytime) {
            return (
                dish.status === OrderItemStatus.Cancelled ||
                dish.status === OrderItemStatus.Closed ||
                dish.status === OrderItemStatus.Delivered
            );
        } else {
            return (
                dish.status === OrderItemStatus.Cancelled ||
                dish.status !== OrderItemStatus.Ready ||
                orderDetails.responsibleUserId !== user.authUserId
            );
        }
    }

    function renderDeliveredCheckbox(dish: OrderItem, dailyMenuId?: string) {
        const isDisabled = checkIsDeliveryDisabled(dish);
        if (
            !editOrder &&
            orderDetails.service === OrderServices.DineIn &&
            (((restaurant as any)?.[serviceKey]?.isDeliverAnytime &&
                currentRestaurant?.currentPosition !== Positions.COOK) ||
                (orderDetails.responsibleUserId === user.authUserId &&
                    dish.status === OrderItemStatus.Ready) ||
                dish.status === OrderItemStatus.Delivered ||
                dish.status === OrderItemStatus.Closed)
        ) {
            if (!!dailyMenuId) {
                return (
                    <>
                        <Checkbox
                            onChange={() =>
                                handleCheckingItems(
                                    dish.id,
                                    '',
                                    OrderItemStatus.Delivered,
                                    dailyMenuId
                                )
                            }
                            className="dailyMenuDeliveryCheckbox"
                            classes={{ root: classes.dailyMenuDeliveryCheckbox }}
                            inputProps={{
                                'aria-label': 'select all desserts',
                            }}
                            checked={
                                dish.status === OrderItemStatus.Delivered ||
                                dish.status === OrderItemStatus.Closed
                            }
                            disabled={isDisabled}
                        />
                    </>
                );
            } else {
                return (
                    <Checkbox
                        onChange={() =>
                            handleCheckingItems(dish.id, dish.uniqueId!, OrderItemStatus.Delivered)
                        }
                        style={{ paddingRight: 0 }}
                        inputProps={{ 'aria-label': 'select all desserts' }}
                        checked={
                            dish.status === OrderItemStatus.Delivered ||
                            dish.status === OrderItemStatus.Closed
                        }
                        disabled={isDisabled}
                    />
                );
            }
        }
    }

    function prepareOrderDishesList(
        categoryName: string,
        dishes: OrderItem[],
        guestNumber: number,
        dailyMenu?: OrderDetailsItem
    ) {
        let renderedCategoryName: string | undefined =
            categoryName !== 'null' ? categoryName : 'no category';
        if (dailyMenu) {
            renderedCategoryName = dailyMenu.name;
        }

        dishes = dishes.sort((a, b) => {
            const getPriority = (obj: OrderItem) => {
                if (obj.isPaid) return 0;
                if (obj.dishes?.some((item) => item.isPaid)) return 1;
                return 2;
            };

            return getPriority(a) - getPriority(b);
        });

        function showCategoryName() {
            if (selectedFilter.length === 0) {
                return true;
            }
            if (selectedFilter.some((f) => f.id === dishes[0].categoryId)) {
                return true;
            }
            if (dailyMenu && selectedFilter.some((f) => f.id === dailyMenuKey)) {
                return true;
            }
            if (selectedFilter.some((f) => f.id === courseMenuKey)) {
                return true;
            }
            if (guestNumber > 0 && filterDishes(dishes)) {
                return true;
            }
        }

        function shouldRenderModifiedIngredients(dish: OrderItem) {
            return dish.modifiedIngredients && dish.modifiedIngredients.length > 0;
        }

        function prepareItemStatusClass(item: OrderItem, dailyMenuPaid?: boolean) {
            if (item.status === OrderItemStatus.Ready) {
                return classes.orderItemCardReady;
            } else if (
                item.status === OrderItemStatus.Delivered ||
                item.status === OrderItemStatus.Closed ||
                item.status === OrderItemStatus.Cancelled
            ) {
                return classes.orderItemCardDeliveredOrClosed;
            } else if (item.isPaid || dailyMenuPaid) {
                return classes.orderItemCardPaid;
            } else {
                return null;
            }
        }

        function renderDaily(dailyMenu: OrderDetailsItem) {
            return (
                <OrderDailyMenuItem
                    dailyMenu={dailyMenu}
                    dishes={dishes}
                    editOrder={editOrder}
                    editedComment={editedComment}
                    handleCheckingItems={handleCheckingItems}
                    checkIsDisableDailyMenuItem={checkIsDisableDailyMenuItem}
                    renderDeliveredCheckbox={renderDeliveredCheckbox}
                    renderEditedIngredients={renderEditedIngredients}
                    renderComment={renderComment}
                    prepareItemStatusClass={prepareItemStatusClass}
                    HighlitedText={HighlitedText}
                    dailyMenuKey={dailyMenuKey}
                    selectedFilter={selectedFilter.map((f) => f.id)}
                    restaurant={restaurant}
                    orderDetails={orderDetails}
                    user={user}
                    currentPosition={currentPosition}
                />
            );
        }

        function renderItems() {
            return dishes.map((dish, index) => (
                <OrderItemCard
                    key={dish.id + index}
                    dish={dish}
                    index={index}
                    className={prepareItemStatusClass(dish)}
                    isDisabled={
                        selectedFilter.length > 0 &&
                        !selectedFilter
                            .map((f) => f.id)
                            .includes(
                                category.categoryDetails?.dishes?.filter((x) => x.id === dish.id)[0]
                                    ?.categoryId!
                            )
                    }
                    isFullDisabled={checkIsDisabled(dish.status, dish.categoryId)}
                    paddingBottom={editedComment === dish.uniqueId ? 8 : 0}
                    comment={renderComment(dish)}
                    isAllowed={checkIsAllowed(dish)}
                    isEditOrder={editOrder}
                    priceText={
                        <PriceText
                            price={dish.price}
                            isPaid={dish.isPaid || orderDetails.isPaid}
                            itemStatus={dish.status}
                            isStorno={dish.status === OrderItemStatus.Storno}
                        />
                    }
                    highlitedText={HighlitedText}
                    addItemToOrder={(dish: OrderItem, amount: number) =>
                        dispatch(
                            ordersStore.actionCreators.addItemToOrder(dish, amount, () =>
                                setHasChanged(true)
                            )
                        )
                    }
                    handleCheckingItems={handleCheckingItems}
                    editedIngredients={
                        shouldRenderModifiedIngredients(dish) ? renderEditedIngredients(dish) : null
                    }
                    deliveredCheckbox={renderDeliveredCheckbox(dish)}
                />
            ));
        }

        function renderMultiCourseModeItems() {
            const grouped: Array<OrderItem[]> = [];
            dishes
                .filter((d) => hasFilter(d))
                .forEach((d) => {
                    if (!d.courseNumber) return;
                    var existing = grouped[d.courseNumber];
                    if (existing) {
                        existing.push(d);
                    } else {
                        grouped[d.courseNumber] = [d];
                    }
                });

            return grouped.map((_dishes, index) => {
                return (
                    <>
                        <Typography variant="subtitle2">
                            {i18n.t('common.course')} {index}
                        </Typography>
                        {_dishes.map((dish, index) => (
                            <OrderItemCard
                                key={dish.id + index}
                                dish={dish}
                                index={index}
                                className={prepareItemStatusClass(dish)}
                                isDisabled={
                                    selectedFilter.length > 0 &&
                                    !selectedFilter
                                        .map((f) => f.id)
                                        .includes(
                                            category.categoryDetails?.dishes?.filter(
                                                (x) => x.id === dish.id
                                            )[0]?.categoryId!
                                        )
                                }
                                isFullDisabled={checkIsDisabled(dish.status, dish.categoryId)}
                                paddingBottom={editedComment === dish.uniqueId ? 8 : 0}
                                comment={renderComment(dish)}
                                isAllowed={checkIsAllowed(dish)}
                                isEditOrder={editOrder}
                                priceText={
                                    <PriceText
                                        price={dish.price}
                                        isPaid={dish.isPaid || orderDetails.isPaid}
                                        itemStatus={dish.status}
                                        isStorno={dish.status === OrderItemStatus.Storno}
                                    />
                                }
                                highlitedText={HighlitedText}
                                addItemToOrder={(dish: OrderItem, amount: number) =>
                                    dispatch(
                                        ordersStore.actionCreators.addItemToOrder(
                                            dish,
                                            amount,
                                            () => setHasChanged(true)
                                        )
                                    )
                                }
                                handleCheckingItems={handleCheckingItems}
                                editedIngredients={
                                    shouldRenderModifiedIngredients(dish)
                                        ? renderEditedIngredients(dish)
                                        : null
                                }
                                deliveredCheckbox={renderDeliveredCheckbox(dish)}
                            />
                        ))}
                    </>
                );
            });
        }

        return (
            <div key={categoryName + Math.random() * 123}>
                {showCategoryName() &&
                    (editOrder && dailyMenu ? (
                        <div
                            className={classes.flexRowSpaceBetween}
                            style={{ padding: '0px 14px 0 6px' }}
                        >
                            <div className={classes.orderDetailsGroupText}>
                                <img
                                    style={{ marginRight: 18 }}
                                    src={images.icons.removeCircle}
                                    alt="remove-circle"
                                    onClick={() =>
                                        checkIsAllowed(dailyMenu!) &&
                                        dispatch(
                                            ordersStore.actionCreators.addItemToOrder(dailyMenu, 0)
                                        )
                                    }
                                    className={clsx(classes.removeOrderItem, {
                                        Disabled: !checkIsAllowed(dailyMenu),
                                    })}
                                />
                                {renderedCategoryName}{' '}
                            </div>
                            <div
                                className={classes.orderItemAmount}
                                style={{ width: 80, margin: '0 0 10px' }}
                            >
                                <span
                                    className={clsx(classes.orderItemAmountControl, {
                                        Disabled:
                                            dailyMenu?.amount === 0 || !checkIsAllowed(dailyMenu),
                                    })}
                                    onClick={() =>
                                        dailyMenu?.amount - 1 >= 0 &&
                                        dispatch(
                                            ordersStore.actionCreators.addItemToOrder(
                                                dailyMenu,
                                                -1,
                                                () => setHasChanged(true)
                                            )
                                        )
                                    }
                                >
                                    -
                                </span>
                                <span
                                    className={clsx(classes.orderItemAmountNumber, {
                                        [classes.textLight]:
                                            dailyMenu?.status === OrderItemStatus.Ready,
                                    })}
                                >
                                    {dailyMenu?.amount || 0}
                                </span>
                                <span
                                    className={clsx(classes.orderItemAmountControl, {
                                        Disabled: !checkIsAllowed(dailyMenu),
                                    })}
                                    onClick={() =>
                                        dispatch(
                                            ordersStore.actionCreators.addItemToOrder(
                                                dailyMenu,
                                                1,
                                                () => setHasChanged(true)
                                            )
                                        )
                                    }
                                >
                                    +
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className={classes.orderDetailsGroupText}>{renderedCategoryName} </div>
                    ))}
                {dailyMenu
                    ? renderDaily(dailyMenu)
                    : guestNumber > 0
                    ? renderMultiCourseModeItems()
                    : renderItems()}
            </div>
        );
    }

    function renderComment(dish: OrderItem, containerStyle?: React.CSSProperties) {
        if (!dish) return null;
        const isActive = editedComment === dish.uniqueId;
        const isEditAllowed =
            !orderDetails.isPaid &&
            currentPosition !== Positions.COOK &&
            orderDetails.responsibleUserId === user.authUserId;
        if (!isEditAllowed && !dish.comment?.length) {
            return null;
        } else if (!isEditAllowed && dish.comment?.length) {
            return (
                <div className={classes.commentSection} style={containerStyle}>
                    <div className={classes.orderDetailsHeadingText}>
                        {i18n.t('common.itemNotes')}
                    </div>
                    <Box className={classes.mVertical10} fontSize={14}>
                        {dish.comment}
                    </Box>
                </div>
            );
        }

        return (
            <div className={classes.commentSection} style={containerStyle}>
                <Formik
                    initialValues={{
                        comment: dish.comment || '',
                    }}
                    validationSchema={validationSchema}
                    onSubmit={(values) => {
                        saveComment(values.comment);
                    }}
                >
                    {({ setFieldValue, submitForm, values, errors }) => (
                        <Form>
                            <FormControl fullWidth variant="standard">
                                <InputLabel
                                    htmlFor="filled-adornment-password"
                                    className={classes.orderDetailsHeadingText}
                                >
                                    {values.comment.length > 0 && !isActive
                                        ? i18n.t('common.itemNotes')
                                        : i18n.t('common.addNote')}
                                </InputLabel>
                                <Input
                                    multiline
                                    disabled={!isActive}
                                    disableUnderline={!isActive}
                                    autoFocus={isActive}
                                    error={!!errors.comment}
                                    id="filled-adornment-password"
                                    type="text"
                                    placeholder="Comment"
                                    value={values.comment}
                                    onChange={(e) => setFieldValue('comment', e.target.value)}
                                    endAdornment={
                                        <InputAdornment
                                            position="end"
                                            style={{ marginRight: isActive ? 7 : -3, width: 25 }}
                                        >
                                            {isActive ? (
                                                <>
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={removeComment}
                                                    >
                                                        <img
                                                            src={images.icons.removeCircle}
                                                            alt="remove-circle"
                                                            style={{
                                                                marginRight: 5,
                                                            }}
                                                        />
                                                    </IconButton>
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={() => submitForm()}
                                                    >
                                                        <img
                                                            height={13}
                                                            src={images.icons.checkedCircle}
                                                            alt="remove-circle"
                                                        />
                                                    </IconButton>
                                                </>
                                            ) : (
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setEditedComment(dish.uniqueId!)}
                                                >
                                                    <img
                                                        src={images.icons.comment}
                                                        alt="remove-circle"
                                                        style={{ marginRight: 0 }}
                                                    />
                                                </IconButton>
                                            )}
                                        </InputAdornment>
                                    }
                                />
                            </FormControl>
                        </Form>
                    )}
                </Formik>
            </div>
        );
    }

    function hasFilter(dish: OrderItem) {
        if (selectedFilter.length === 0) {
            return true;
        }
        if (
            selectedFilter.some((f) => f.id === dailyMenuKey) &&
            dish.dailyMenuId &&
            dish.menuType !== MenuType.MultiCourse
        ) {
            return true;
        }
        if (
            selectedFilter.some((f) => f.id === courseMenuKey) &&
            dish.menuType === MenuType.MultiCourse
        ) {
            return true;
        }
        return selectedFilter.length > 0 && selectedFilter.some((f) => f.id === dish.categoryId);
    }

    function getMenuDishes(dailyMenu = false) {
        return prepareDishesData().filter((each) => {
            let isDailyMenu;
            each.items.forEach((orderItem) => {
                isDailyMenu = dailyMenu ? orderItem.dailyMenuId : !orderItem.dailyMenuId;
            });
            return isDailyMenu;
        });
    }

    function filterDishes(dishes: OrderItem[]) {
        let shouldBeFiltered = false;
        dishes.forEach((dish) => {
            if (hasFilter(dish)) {
                shouldBeFiltered = true;
            }
        });
        if (dishes[0].dailyMenuId) {
            const otherDishes = getMenuDishes();
            dishes.forEach((eachDailyMenuDish) => {
                otherDishes.forEach((dish) => {
                    const commonItem = dish.items.find((dish) => dish.id === eachDailyMenuDish.id);
                    if (commonItem && hasFilter(commonItem)) {
                        shouldBeFiltered = true;
                    }
                });
            });
        }
        return shouldBeFiltered;
    }

    function checkIfOrderEdited() {
        dispatch(ordersStore.actionCreators.addItemToEditedOrder(null));
        setShowMenus(false);
        /* let edited = false;
        orderDetails.orderItems.forEach((each) => {
            if (each.modified) {
                edited = true;
                return;
            }
        });
        if (edited && showCancelConfirmation === 0) {
            return setShowCancelConfirmation(1);
        }
        setShowCancelConfirmation(2);
        setShowMenus(false);
        cancelChanges(); */
    }

    function calculateItemPrice(item: OrderItem) {
        let price = item.price;
        if (!!item?.dishes?.length) {
            item.dishes.forEach((dish) => {
                if (!!dish.modifiedIngredients?.length) {
                    dish.modifiedIngredients.forEach((modIngr) => {
                        const ingredient = dish.ingredients?.find((ingr) => ingr.id === modIngr.id);
                        if (modIngr.isAdditionChecked) {
                            price += ingredient.price;
                        } else {
                            price -= ingredient.price;
                        }
                    });
                }
            });
        } else if (!!item.modifiedIngredients?.length) {
            item.modifiedIngredients.forEach((modIngr) => {
                let ingredient = item.ingredients?.find((ingr) => ingr.id === modIngr.id);
                if (!ingredient) {
                    ingredient = modIngr;
                }
                if (modIngr.isAdditionChecked) {
                    price += ingredient.price;
                } else {
                    price -= ingredient.price;
                }
            });
        }
        return price;
    }

    function calculateTotalPrice() {
        return (
            orderDetails.orderItems.reduce(
                (accum, curr) => (accum += calculateItemPrice(curr) * 100 * curr.amount),
                0
            ) / 100
        );
    }

    function showDeliverAllCheckbox() {
        if (orderDetails.service === OrderServices.DineIn) {
            let notDeliveredItems: OrderItem[] = [];
            for (let item of orderDetails.orderItems) {
                if (!!item.dishes?.length) {
                    for (let dish of item.dishes) {
                        if (
                            dish.status !== OrderItemStatus.Delivered &&
                            dish.status !== OrderItemStatus.Storno &&
                            dish.status !== OrderItemStatus.Cancelled
                        ) {
                            notDeliveredItems.push(dish);
                        }
                    }
                } else {
                    if (
                        item.status !== OrderItemStatus.Delivered &&
                        item.status !== OrderItemStatus.Storno &&
                        item.status !== OrderItemStatus.Cancelled
                    ) {
                        notDeliveredItems.push(item);
                    }
                }
            }
            const readyItems: OrderItem[] = [];
            for (let item of orderDetails.orderItems) {
                if (!!item.dishes?.length) {
                    readyItems.push(
                        ...item.dishes.filter((x) => x.status === OrderItemStatus.Ready)
                    );
                } else {
                    if (item.status === OrderItemStatus.Ready) {
                        readyItems.push(item);
                    }
                }
            }
            if (
                (restaurant as any)?.[serviceKey]?.isDeliverAnytime ||
                (!!readyItems.length && !!notDeliveredItems.length)
            ) {
                if (!!notDeliveredItems.length) {
                    return (
                        <FormControlLabel
                            style={{ flexDirection: 'row-reverse', marginRight: -10 }}
                            control={
                                <Checkbox
                                    onChange={() =>
                                        dispatch(
                                            ordersStore.actionCreators.deliverOrderItems(
                                                orderDetails.id
                                            )
                                        )
                                    }
                                    checked={!!!notDeliveredItems.length}
                                />
                            }
                            label={i18n.t('common.deliverAll')}
                        />
                    );
                }
            }
        }
    }

    return (
        <Card
            className={classes.root}
            style={
                user.role === Roles.EMPLOYEE
                    ? { backgroundColor: 'transparent', marginLeft: 8 }
                    : {}
            }
        >
            <div
                className={`${classes.orderDetailsHeader} ${classes.flexRowSpaceBetween}`}
                style={
                    user.role === Roles.EMPLOYEE
                        ? { backgroundColor: theme.palette.common.white }
                        : {}
                }
            >
                <ServiceType
                    order={orderDetails}
                    onChange={changeServiceTypeMethod}
                    isEditable={orderDetails.status !== OrderStatus.Cancelled}
                />
                <div
                    style={{
                        pointerEvents: orderDetails.id.length > 0 ? 'auto' : 'none',
                        flexGrow: 1,
                    }}
                    className={classes.flexRow}
                >
                    <div
                        style={{
                            paddingTop: 0,
                            paddingBottom: 0,
                            height: 52,
                            marginTop: 0,
                            marginBottom: 0,
                            flexGrow: 1,
                            /*  width: 170 */
                        }}
                        className={`${classes.flexColumn} ${classes.orderHeaderItem}`}
                    >
                        <OrderQRLeftCode
                            order={orderDetails}
                            submit={changeOrderQRCode}
                            isDisabled={!(user.role === Roles.EMPLOYEE)}
                        />
                        <span className={classes.orderNumber}>{`№ ${
                            orders.orderDetailsId ? orderDetails.orderNumber : ''
                        }`}</span>
                    </div>

                    <OrderTime order={orderDetails} />
                    <DeliveryTime
                        order={orderDetails}
                        submit={setDeliveryTime}
                        disabled={user.role === Roles.OWNER}
                    />
                    <OrderStatusItem
                        update={saveChanges}
                        order={orderDetails}
                        isDisabled={!ableToChangeStatus}
                    />
                </div>
            </div>
            <div
                className={classes.orderItemsDetailsContainer}
                style={
                    user.role === Roles.EMPLOYEE
                        ? {
                              backgroundColor: theme.palette.common.white,
                              height: '90%',
                              marginTop: 8,
                          }
                        : { height: '90%' }
                }
            >
                <div
                    className={classes.orderItemsDetails}
                    style={user.role === Roles.EMPLOYEE ? { flexGrow: 1 } : {}}
                >
                    {prepareOrderItemsHeader()}
                    {showDeliverAllCheckbox()}
                    <div className={classes.orderItemsContainer}>
                        <div
                            style={{
                                visibility: orders.orderDetailsId ? 'visible' : 'hidden',
                                /* overflow: 'auto', */
                                padding: 2,
                                //maxHeight: 500,
                            }}
                            ref={listRef}
                        >
                            {prepareDishesData()
                                .filter((x) => filterDishes(x.items))
                                .map((x) =>
                                    prepareOrderDishesList(
                                        x.name,
                                        x.items,
                                        x.guestNumber,
                                        x.dailyMenu
                                    )
                                )}
                        </div>
                    </div>
                    <div className={classes.orderItemsFooter}>
                        <div className={classes.orderDetailsHeadingText}>
                            <span style={{ fontSize: '10px', lineHeight: '12px' }}>{`${i18n.t(
                                'common.numberOfDishes'
                            )}: `}</span>
                            <span style={{ fontWeight: 900, fontSize: 14 }}>
                                {orders.orderDetailsId && orderDetails.orderItems?.length
                                    ? orderDetails.orderItems
                                          .map((x) => x.amount)
                                          .reduce((x, y) => x + y)
                                    : 0}
                            </span>
                        </div>
                        <Card className={classes.orderItemsFooterCard}>
                            <div className={classes.orderItemCardTitle}>
                                {i18n.t('common.totalPrice')}:
                            </div>
                            <div className={classes.orderItemsFooterCardPrice}>
                                {' '}
                                CHF{' '}
                                <span>
                                    {orders.orderDetailsId
                                        ? priceStyleHelper(
                                              editOrder
                                                  ? calculateTotalPrice()
                                                  : orderDetails.totalAmount,
                                              true
                                          )
                                        : 0}
                                </span>
                            </div>
                        </Card>
                    </div>
                </div>
                <div className={classes.customerDataContainer}>
                    <div className={classes.customerData}>
                        <ResponsibleUserSelect />
                        {orderDetails.service !== OrderServices.HomeDelivery && (
                            <>
                                <br />
                                <PriceLevelSelect />
                            </>
                        )}
                        <span
                            className={classes.orderDetailsHeadingText}
                            style={{ marginBottom: 20, marginTop: 20 }}
                        >
                            {i18n.t('common.paymentStatus')}
                        </span>
                        <OrderPayment
                            isPaid={orderDetails.isPaid}
                            totalAmount={
                                editOrder ? calculateTotalPrice() : orderDetails.totalAmount
                            }
                        />
                        <div className={classes.orderDetailsHeadingText}>
                            {i18n.t('common.clientNotes')}
                        </div>
                        {orders.orderDetails && !!orderDetails.comment && (
                            <Text
                                text={orderDetails.comment}
                                className={classes.customerNote}
                                whiteSpace={'normal'}
                                fontSize={14}
                                maxLength={70}
                            />
                        )}
                        <div className={classes.orderDetailsHeadingText} style={{ marginTop: 10 }}>
                            {i18n.t('common.clientInformation')}
                        </div>
                        <div className={classes.customerDataItem}>
                            <div style={{ backgroundImage: `url(${images.icons.user})` }} />
                            <span>
                                {orders.orderDetailsId &&
                                    HighlitedText(
                                        `${orderDetails.firstName} ${orderDetails.lastName}`
                                    )}
                            </span>
                        </div>
                        <div className={classes.customerDataItem}>
                            <div style={{ backgroundImage: `url(${images.icons.marker})` }} />
                            <span>
                                {orders.orderDetailsId &&
                                    HighlitedText(
                                        `${orderDetails.addressLine} ${orderDetails.zip} ${orderDetails.city}`
                                    )}
                            </span>
                        </div>
                        <div className={classes.customerDataItem}>
                            <div style={{ backgroundImage: `url(${images.icons.email})` }} />
                            <span>
                                {orders.orderDetailsId && HighlitedText(orderDetails.email)}
                            </span>
                        </div>
                        <div className={classes.customerDataItem}>
                            <div style={{ backgroundImage: `url(${images.icons.phone})` }} />
                            <span>
                                {orders.orderDetailsId && HighlitedText(orderDetails.phoneNumber)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            {currentPosition !== Positions.COOK && (
                <StornoModal
                    open={showStornoModal}
                    onClose={() => dispatch(ordersStore.actionCreators.SetShowStornoModal(false))}
                    orderItems={orderDetails.orderItems || []}
                    orderId={orderDetails.id}
                    allowPaidStorno={currentPosition === Positions.MANAGER}
                />
            )}
            <MenusModal
                isOpen={showMenus}
                onClose={() => {
                    checkIfOrderEdited();
                }}
                saveChanges={saveChanges}
            />
            <PopupConfirmation
                open={showCancelConfirmation === 1}
                close={() => {
                    setShowCancelConfirmation(2);
                }}
                action={() => {
                    setShowCancelConfirmation(2);
                    setShowMenus(false);
                    setShowIngredients(false);
                    cancelChanges();
                }}
                title={i18n.t('warnings.cancelChanges')}
                description={i18n.t('warnings.areYouSureToCloseWithoutChanges')}
                activeBtn={i18n.t('button.yesClose')}
                basicButton
            />
            <Dialog maxWidth="sm" open={showIngredients} onClose={() => setShowIngredients(false)}>
                <div className={classes.flexRowSpaceBetween}>
                    <DialogTitle style={{ paddingLeft: 14 }}>{i18n.t('common.edit')}</DialogTitle>
                    <DialogActions style={{ marginBottom: 0, paddingRight: 14 }}>
                        <Button color="primary" variant="text" onClick={handleIngredientsClose}>
                            {i18n.t('button.cancel')}
                        </Button>
                        <Button
                            variant={'contained'}
                            color="primary"
                            style={{ width: '92px' }}
                            onClick={() => {
                                setShowIngredients(false);
                                saveChanges();
                            }}
                        >
                            {i18n.t('common.update')}
                        </Button>
                    </DialogActions>
                </div>
                <DialogContent className={classes.dishIngredientDialog}>
                    <MenuIngredientManagement />
                </DialogContent>
            </Dialog>

            <Dialog
                maxWidth="sm"
                open={showDiscountModal}
                onClose={() => setShowDiscountModal(false)}
            >
                <DialogTitle style={{ paddingLeft: 14 }}>{i18n.t('common.discount')}</DialogTitle>
                <DialogContent>
                    <Formik
                        initialValues={{
                            discountAmount: 0,
                        }}
                        onSubmit={(values) => {
                            console.log(values.discountAmount);
                        }}
                    >
                        {({ setFieldValue, submitForm, values, errors }) => (
                            <Form>
                                <FormControl fullWidth variant="standard">
                                    <BaseInput
                                        error={errors.discountAmount}
                                        variant="filled"
                                        fullWidth={true}
                                        name={`discountAmount`}
                                        type="number"
                                        min={0}
                                        label={i18n.t('common.discountAmount')}
                                        placeholder={i18n.t('common.discountAmount')}
                                    />
                                </FormControl>
                                <br></br>
                                <DialogActions>
                                    <Button
                                        style={{ marginRight: 15 }}
                                        variant="text"
                                        color="primary"
                                        onClick={() => setShowDiscountModal(false)}
                                    >
                                        {i18n.t('button.cancel')}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={submitForm}
                                        disabled={values.discountAmount <= 0}
                                    >
                                        {i18n.t('common.apply')}
                                    </Button>
                                </DialogActions>
                            </Form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>
            <div style={{ display: 'none' }} />
        </Card>
    );
}
